import { Box, Typography, Button, Toolbar, IconButton, CircularProgress } from "@mui/material";
import { useState, useMemo } from "react";
import { StoplossClaim } from "@/types";
import SendIcon from "@mui/icons-material/Send";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";
import { useSubmitStoplossClaims } from "@/hooks/useSubmitStoplossClaims";

interface StoplossClaimsBulkActionsProps {
  selectedIds: string[];
  onUnselectAll: () => void;
  allClaims: StoplossClaim[];
}

type BulkTrack = "aura" | "carrier";

/**
 * Dual bulk-action bar (#5381): two explicit, independent track actions — no
 * dialog, no "also submit to Aura" checkbox, no submit/submit ambiguity.
 *   • "Advance funding · Aura" (teal/primary) → POST /stoploss-claims/submit-to-aura
 *   • "Mark submitted · Carrier" (secondary)  → POST /stoploss-claims/mark-submitted
 * Per-track skip/partial reporting comes from useSubmitStoplossClaims (the carrier
 * endpoint skips already-submitted and reports actioned-vs-skipped; the Aura sender
 * surfaces partial failures). On a clean success the selection is cleared; on a
 * partial/failed result it is kept so the user can see what remains and retry
 * (safe because both endpoints are idempotent per claim/group).
 */
export const StoplossClaimsBulkActions = ({
  selectedIds,
  onUnselectAll,
  allClaims,
}: StoplossClaimsBulkActionsProps) => {
  const { submit } = useSubmitStoplossClaims();
  const [pendingTrack, setPendingTrack] = useState<BulkTrack | null>(null);

  const selectedClaims = useMemo(
    () => allClaims.filter((claim) => selectedIds.includes(claim.id)),
    [allClaims, selectedIds]
  );

  const runBulk = async (track: BulkTrack) => {
    setPendingTrack(track);
    try {
      const success = await submit(selectedClaims, track);
      if (success) onUnselectAll();
    } finally {
      setPendingTrack(null);
    }
  };

  const busy = pendingTrack !== null;

  return (
    <Toolbar
      className="RaBulkActionsToolbar-toolbar"
      sx={{
        alignItems: "center",
        justifyContent: "space-between",
        backgroundColor: (theme) => theme.palette.primary.main + "14", // 8% opacity in hex
        minHeight: "48px",
        px: 2,
      }}
    >
      <Box className="RaBulkActionsToolbar-title" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <IconButton
          size="small"
          onClick={onUnselectAll}
          color="primary"
          aria-label="Clear selection"
          title="Clear selection"
          className="RaBulkActionsToolbar-icon"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <Typography variant="body1">
          {selectedIds.length} item{selectedIds.length !== 1 ? "s" : ""} selected
        </Typography>
      </Box>

      <Toolbar className="RaBulkActionsToolbar-topToolbar" variant="dense" sx={{ gap: 1, minHeight: "auto", px: 0 }}>
        <Button
          startIcon={pendingTrack === "aura" ? <CircularProgress size={14} color="inherit" /> : <CloudUploadIcon />}
          onClick={() => runBulk("aura")}
          size="small"
          color="primary"
          disabled={busy}
        >
          Advance funding · Aura
        </Button>
        <Button
          startIcon={pendingTrack === "carrier" ? <CircularProgress size={14} color="inherit" /> : <SendIcon />}
          onClick={() => runBulk("carrier")}
          size="small"
          color="secondary"
          disabled={busy}
        >
          Mark submitted · Carrier
        </Button>
      </Toolbar>
    </Toolbar>
  );
};
